import { text4, text5, bg0, bg1 } from '@osmosis/tailwind-ui/styles';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { H3 } from '@osmosis/tailwind-ui/components/Text';

import { Input } from '@osmosis/tailwind-ui/components/Input';
import { LoadingSpinner } from '@osmosis/tailwind-ui/components/LoadingSpinner';
import React, {
  ChangeEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import Divider from '../../../../components/Divider';
import { FormattedMessage, useIntl } from 'react-intl';
import FocusLock from 'react-focus-lock';
import { useFetch } from '../../../../context/FetchProvider';
import useDebounce from '../../../../hooks/useDebounce';
import { PlaylistSearchHitType } from '../../../../types/search';
import SearchResults from './SearchResults';
import {
  PlaylistLearnNodeItem,
  PlaylistProps,
} from '@osmosis/shared/types/playlist';
import { useLanguageContext } from '../../../../context/LanguageProvider';
import { handleEscapeKey } from '../../../../lib/util/eventHandlers';
import { filterTBRSearchHits } from '../../../_lib/frontend/retired-page';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';

type Props = {
  isVisible: boolean;
  hide: () => void;
  playlist: PlaylistProps;
  handleRemove: (items: PlaylistLearnNodeItem[]) => Promise<void>;
};

/**
 * Playlist Search - A component to search playlists.
 */
const PlaylistSearch = (props: Props) => {
  const { isVisible, hide, playlist, handleRemove } = props;
  const { get } = useFetch();
  const { activeLanguage } = useLanguageContext();
  const intl = useIntl();
  const searchLabel = intl.formatMessage({
    id: 'learn.playlist.search.search_and_add_content',
    defaultMessage: 'Search and add content',
  });

  const [searchHits, setSearchHits] = useState<PlaylistSearchHitType[]>([]);

  const [searchValue, setSearchValue] = useState('');
  const debouncedValue = useDebounce(searchValue, 700);

  const [isLoading, setLoading] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.currentTarget.value);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    handleEscapeKey(e, () => hide?.());
  };

  const search = useCallback(
    async (val: string) => {
      if (!val) {
        setLoading(false);

        return;
      }

      setLoading(true);

      const { data } = await get<{ hits: PlaylistSearchHitType[] }>(
        '/data/search',
        {
          params: {
            q: val,
            watchStatus: true,
            language: activeLanguage,
            searchFilters: localStorage.getItem('playlistItem')
              ? ['subProducts::Clinical Sciences']
              : [],
          },
        },
      );

      const { hits } = data;

      let filteredHits = hits.filter((hit) => hit.category !== 'answers');
      if (localStorage.getItem('playlistItem')) {
        filteredHits = filteredHits.filter(filterTBRSearchHits);
      }

      setSearchHits(filteredHits);

      if (data) setLoading(false);
    },
    [activeLanguage, get],
  );

  useEffect(() => {
    search(debouncedValue);
  }, [debouncedValue, search]);

  useEffect(() => {
    const savedPlaylistItem = localStorage.getItem('playlistItem');
    if (savedPlaylistItem) setSearchValue(JSON.parse(savedPlaylistItem).title);
    if (!isVisible) {
      localStorage.removeItem('playlistItem');
      setSearchValue('');
    }
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          role="dialog"
          aria-modal="true"
          onKeyDown={handleKeyDown}
          className="absolute right-0 top-0 z-20 origin-right"
          initial={{ opacity: 0.5, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          exit={{
            opacity: 0.5,
            scaleX: 0,
          }}
          transition={{ ease: 'easeInOut', duration: 0.3 }}
          key="snackbar"
        >
          <FocusLock returnFocus={true}>
            <Box className="lg:w-128 w-full overflow-hidden rounded-bl-md border border-r-0 border-gray-200 md:w-96">
              <Flex className={`items-center gap-4 px-2 py-2 ${bg1}`}>
                <Input
                  data-testid="playlist-search-input"
                  aria-label={searchValue ? searchLabel : undefined}
                  value={searchValue}
                  onChange={handleChange}
                  placeholder={searchLabel}
                  className={`bg-bg-0 h-8 pl-3 pr-2 text-sm ${text4}`}
                />

                <IconButton
                  type="button"
                  variant="transparent"
                  icon={<Icon icon={faXmark} />}
                  onClick={hide}
                  data-testid="close-playlist-btn"
                  aria-label={intl.formatMessage({
                    id: 'close-playlist-btn',
                    defaultMessage: 'Close search and add content',
                  })}
                  className={`${text5} text-md flex-shrink-0`}
                />
              </Flex>
              <Flex
                className={`${bg0} max-h-128 flex-col overflow-auto p-4 ${
                  isLoading ? 'items-center' : 'items-start'
                }`}
              >
                {!isLoading && debouncedValue && (
                  <>
                    <H3 className={`${text4} mb-4`}>
                      <FormattedMessage
                        id="learn.playlist.search.search_results_for"
                        defaultMessage="Search results for "
                      />

                      {debouncedValue}
                    </H3>
                    <SearchResults
                      hits={searchHits}
                      playlist={playlist}
                      handleRemove={handleRemove}
                    />
                  </>
                )}

                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <H3 className={`mb-4`}>
                      <FormattedMessage
                        id="learn.playlist.search.recent_searches"
                        defaultMessage="Recent searches"
                      />
                    </H3>
                    <Divider className={`mb-3 ${bg1}`} />
                  </>
                )}
              </Flex>
            </Box>
          </FocusLock>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlaylistSearch;
